<template>
  <div class="view-subscriptions">
    <h1 class="mt-0">{{ $t("headings.subscriptions") }}</h1>

    <section
      v-if="validSubscriptions.length == 0 && providedLicenses.length == 0 && expiredSubscriptions.length > 0"
      class="message-error mb-6 p-4"
    >
      <h3 class="mt-2">{{ $t("messages.noSubscription") }}</h3>
      <p>
        {{ $t("texts.subscriptionExpired") }}
      </p>
      <p v-if="hasExpiredOrganizationSubscription">
        {{ $t("texts.extendLicense") }}
      </p>

      <router-link
        class="button button_secondary mt-2 mb-2"
        :to="{ name: 'order-subscription' }"
      >
        <span v-if="validSubscriptions.length == 0">{{
          $t("actions.buySubscription")
        }}</span>
        <span v-else>{{ $t("actions.buyAdditionalSubscription") }}</span>
      </router-link>
    </section>

    <template v-if="subscriptionRequests.length > 0">
      <h3 class="mt-0">{{ $t("headings.subscriptionRequests") }}</h3>
      <div class="view-subscriptions__list pb-6">
        <subscription-request-card
          v-for="request in subscriptionRequests"
          :key="request.id"
          :request="request"
          @update:request="updateSubscriptionRequest(request.id, $event)"
          @remove:request="updateSubscriptionRequest(request.id, null)"
        />
      </div>
    </template>

    <h3 class="mt-0" v-if="providedLicenses.length > 0">{{ $t("headings.providedLicenses") }}</h3>
    <div v-if="providedLicenses.length > 0" class="column mb-6">
      <div
        v-for="license of providedLicenses"
        :key="license.id"
        class="provided-license"
      >
        <i18n-t keypath="texts.providesLicense" scope="global">
          <template #provider
            ><router-link
              :to="{
                name: 'settings-organization',
                params: { organizationSlug: license.providedBy?.slug }
              }"
              ><strong>{{ license.providedBy?.name }}</strong></router-link
            ></template
          >
          <template #license
            ><strong>{{
              $t(`subscriptions.${license.plan}.name`)
            }}</strong></template
          >
        </i18n-t>
      </div>
    </div>

    <p v-if="noLicense">
      {{ $t("messages.noSubscriptions") }}
    </p>

    <h3 class="mt-0" v-if="validSubscriptions.length > 0">{{ $t("headings.activeSubscriptions") }}</h3>
    <div class="view-subscriptions__list">
      <subscription-card
        v-for="subscription in validSubscriptions"
        :key="subscription.id"
        :subscription-id="subscription.id"
        @update:subscription="updateSubscription(subscription.id, $event)"
      />
    </div>

    <div>
      <router-link class="button mt-4" :to="{ name: 'order-subscription' }">
        <span v-if="noLicense">{{ $t("actions.buySubscription") }}</span>
        <span v-else>{{ $t("actions.buyAdditionalSubscription") }}</span>
      </router-link>
    </div>

    <div class="mt-auto pt-4">
      <i18n-t keypath="texts.requestQuote" scope="global">
        <template #requestQuote>
          <router-link
            :to="{ name: 'order-subscription', query: { paymentMethod: 'quote'} }"
          >
            {{ $t("actions.requestQuote") }}
          </router-link>
        </template>
      </i18n-t>
    </div>

    <template v-if="expiredSubscriptions.length > 0">
      <hr class="mt-7" />
      <h3 class="mt-0">{{ $t("headings.expiredSubscriptions") }}</h3>
      <div class="view-subscriptions__list pb-6">
        <subscription-card
          v-for="subscription in expiredSubscriptions"
          :key="`${subscription.plan}-${subscription.id}`"
          :subscription-id="subscription.id"
          @update:subscription="updateSubscription(subscription.id, $event)"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { filter, some } from "lodash-es";

import SubscriptionCard from "./SubscriptionCard.vue";
import SubscriptionRequestCard from "./SubscriptionRequestCard.vue";

import { useSubscriptions } from "@/api/subscriptions.store";
import { useOrganizations } from "@manager/organizations/organizations.store";
import { useLicense } from "@/api/license.store";

import type { Subscription, SubscriptionRequest } from "@/api/subscriptions.store";

const { subscriptions, getSubscriptions, getSubscriptionRequests } = useSubscriptions();
const { getOrganizations } = useOrganizations({});
const license = useLicense();

const subscriptionRequests = ref<SubscriptionRequest[]>([]);

getSubscriptions();
getOrganizations();

onMounted(() => {
  getSubscriptionRequests().then((requests) => {
    subscriptionRequests.value = requests;
  });
});

const validSubscriptions = computed(() => {
  return filter(subscriptions, (sub) => !sub.expired);
});
const expiredSubscriptions = computed(() => {
  return filter(subscriptions, (sub) => sub.expired);
});
const hasExpiredOrganizationSubscription = computed(() => {
  return expiredSubscriptions.value.some(
    (sub) => sub.seats != null && sub.seats > 1
  );
});

const noLicense = computed(() => {
  return validSubscriptions.value.length == 0 && providedLicenses.value.length == 0;
});

function updateSubscription(id: number, subscription: Subscription | null) {
  if (subscription == null) {
    delete subscriptions[id];
    return;
  }

  subscriptions[id] = subscription;
}

function updateSubscriptionRequest(id: number, request: SubscriptionRequest | null) {
  if (request == null) {
    subscriptionRequests.value = subscriptionRequests.value.filter((r) => r.id != id);
    return;
  }

  const index = subscriptionRequests.value.findIndex((r) => r.id == id);
  if (index == -1) subscriptionRequests.value.push(request);
  else subscriptionRequests.value[index] = request;
}

// ==============================
// PROVIDED LICENSES

const providedLicenses = computed(() => {
  return license.all.filter((l) => l.providedBy != null && !some(subscriptions, (sub) => sub.id == l.providedBy?.subscriptionId));
});
</script>

<style lang="scss">
.view-subscriptions {
  display: flex;
  flex-direction: column;
  max-width: 70rem;
  &__list {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }
}

.provided-license {
  background-color: var(--color-background-deep);
  border-radius: 0.25rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 0.25rem 0.5rem var(--color-shadow-1);
}
</style>
